'use client';

import { RefObject, useCallback, useEffect, useState } from 'react';
import { useLayoutActionContext, useScrollDirection } from '@headless-workspace/core-ui';
import { SEARCH_MENU_ID } from '../layout';

export const useFixedHeader = (
    fixedHeaderRef: RefObject<HTMLElement | null>,
    persistentBannerRef: RefObject<HTMLElement | null>,
    isSearchOpen: boolean,
) => {
    const [isHeaderFullyVisible, setIsHeaderFullyVisible] = useState(true);
    const [translate, setTranslate] = useState('0');
    const [headerHeight, setHeaderHeight] = useState(0);
    const { scrollDirection, scrollDifference } = useScrollDirection();
    const {
        header: { setHeaderOffset, setHeaderHeight: setContextHeaderHeight },
    } = useLayoutActionContext();

    // Get header (+ search menu if open) height
    const getHeaderHeight = useCallback(() => {
        const searchMenu = document.getElementById(SEARCH_MENU_ID);

        const searchMenuHeight = searchMenu ? searchMenu.getBoundingClientRect().height : 0;
        const fixedHeaderHeight = fixedHeaderRef.current ? fixedHeaderRef.current.getBoundingClientRect().height : 0;
        const height = fixedHeaderHeight + searchMenuHeight;

        setHeaderHeight(height);
        setContextHeaderHeight(height);
    }, [fixedHeaderRef, setContextHeaderHeight]);

    useEffect(() => {
        getHeaderHeight();
    }, [getHeaderHeight]);

    useEffect(() => {
        window.addEventListener('resize', getHeaderHeight);

        return () => {
            window.removeEventListener('resize', getHeaderHeight);
        };
    }, [getHeaderHeight]);

    // Check for desktop header visibility
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsHeaderFullyVisible(entry.isIntersecting);
        });

        if (persistentBannerRef.current) {
            observer.observe(persistentBannerRef.current as HTMLElement);
        }

        return () => observer.disconnect();
    }, [persistentBannerRef]);

    useEffect(() => {
        // If search is open, header is visible
        if (isSearchOpen) {
            setTranslate('translate(0, 0)');
            return;
        }

        if (fixedHeaderRef.current) {
            const currentTranslation = fixedHeaderRef.current.getBoundingClientRect().top;

            // If translation is positive, it means the persistent banner is visible
            // => reset translation to 0
            if (currentTranslation > 0) {
                setTranslate('translate(0, 0)');
                return;
            }

            // Apply translation only if :
            // - scrolling down
            // - header is not yet completely hidden
            if (scrollDirection === 'down' && currentTranslation > -headerHeight) {
                // Translate the header up, until it is completely hidden
                const translation = Math.max(currentTranslation - scrollDifference, -headerHeight);
                setTranslate(`translate(0, ${translation}px)`);
            }

            // Apply translation only if :
            // - scrolling up
            // - header is not yet completely visible
            if (scrollDirection === 'up' && currentTranslation < 0) {
                // Translate the header down, until it is completely visible
                const translation = Math.min(currentTranslation + Math.abs(scrollDifference), 0);
                setTranslate(`translate(0, ${translation}px)`);
            }
        }
    }, [fixedHeaderRef, headerHeight, isSearchOpen, scrollDifference, scrollDirection]);

    // Compute header offset (the height of header visible in the viewport) and store it in layoutContext
    useEffect(() => {
        if (fixedHeaderRef.current) {
            const currentTranslation = fixedHeaderRef.current.getBoundingClientRect().top;
            const headerOffset = headerHeight + currentTranslation;
            setHeaderOffset(headerOffset);
        }
    }, [fixedHeaderRef, headerHeight, setHeaderOffset, translate]);

    return { translate, isHeaderFullyVisible };
};
